import {CallJs} from "../lib/types"
import {joinStrings} from "../lib/utils"
import {Config as AdvancedSearchConfig} from "./advancedSearch/ValueObjects"
import {FolderHierarchy, MemoryFile, MemoryFolder} from '../memory/component/Types'
import * as React from "react"
import * as ReactDOM from "react-dom"
import {HasFilesFolders} from "../memory/component/Types";
import {Notification} from "../lib/sbhtml/notification";

export const renderStatisticsField:CallJs<Stats> = (replace, params) => {
	const node = document.createElement('div')
	replace(node)
	ReactDOM.render(<StatsWidget {...params} />, node)
}

export interface Stats {
	views:number
	viewsInAdvancedSearch:number
	downloads:DownloadStats
	advancedSearchConfig:AdvancedSearchConfig
	showFoldersInTree:boolean
}

interface DownloadStats {
	files:FileFieldStats[]
	filesFolders:FilesFoldersFieldStats[]
}

interface Field {
	fieldName:string
}

interface FileFieldStats {
	fieldName:string
	details:MemoryFile
}

interface FilesFoldersFieldStats extends Field {
	fieldName:string
	details:HasFilesFolders
}

const StatsWidget = (props:Stats) =>
	<ul id="news2-stats-widget" className="stats">
		<li><Views viewCount={props.views} /></li>
		{props.advancedSearchConfig.isEnabled ? <li><ViewsInAdvancedSearch viewCount={props.viewsInAdvancedSearch} /></li> : null}
		<li><FileDownloads {...props.downloads} showFoldersInTree={props.showFoldersInTree} /></li>
	</ul>

interface StatisticProps {
	label:string
	className?:string
	children?:React.ReactNode
}

/**
 * Wraps an individual statistic and prefixes it with a label.
 */
const Statistic = (props:StatisticProps) =>
	<span className={joinStrings(["stat", props.className || ""])}>
		<span className="label">{props.label}: </span>
		<span className="value">{props.children}</span>
	</span >

interface HasViewCount { viewCount:number }

/**
 * Number of times the article has been viewed (i.e. the URL representing the specific article was visited).
 */
const Views = (props:HasViewCount) =>
	<Statistic label="Views">{props.viewCount}</Statistic>

/**
 * Number of times an AJAX request for search results included this article.
 */
const ViewsInAdvancedSearch = (props:HasViewCount) =>
	<Statistic label="Views in Advanced Search Results">{props.viewCount}</Statistic>


interface FileDownloadsProps extends DownloadStats { showFoldersInTree:boolean }

/**
 * Number of downloads for each document linked to this article from the memory module (via spare field manager).
 */
const FileDownloads = (props:FileDownloadsProps) => {

	const files = props.files.filter((file) => file.details != null && file.details.id > 0)
	const filesFolders = props.filesFolders.filter((data) => data.details != null && data.details.files.length > 0 || data.details.folders.length > 0)

	const fileComponents = files.map((file) =>
		<Statistic label={file.fieldName} className="downloads" key={`file-${file.fieldName}`}>
			<SingleFileDownload {...file.details} />
		</Statistic>
	)

	const filesFoldersComponents = filesFolders.map((filesFolders) =>
		<Statistic label={filesFolders.fieldName} key={`files-folders-${filesFolders.fieldName}`}>
			{props.showFoldersInTree
				? <MemoryFilesFoldersField {...filesFolders} key={`files-folders-${filesFolders.fieldName}`}/>
				: <MemoryFilesFoldersFieldFlattened  {...filesFolders} key={`files-folders-${filesFolders.fieldName}`}/>}
		</Statistic>
	)

	const downloadInfo = filesFolders.length == 0 && files.length == 0 ? null : <DownloadInfoMessage />

	return <div>
		{fileComponents}
		{filesFoldersComponents}
		{downloadInfo}
	</div>
}

const DownloadInfoMessage = () =>
	<Notification type="info">
		Download counts include the total number of downloads recorded by the document manager for each file. If a linked file is changed, then the download count will also change.
	</Notification>

/**
 * For a given document in the memory module, how many times it was downloaded.
 */
const SingleFileDownload = (props:MemoryFile) => {
	const s = props.downloadCount == 1 ? '' : 's'
	const label = ` ‐ Downloaded ${props.downloadCount} time${s}`
	return <FileNameWrapper
		label={props.title}
		iconPath="/sb/static/images/icons/file.png"
		isImportant={props.downloadCount > 0}>
		<span className={joinStrings([props.downloadCount > 0 ? 'more-important' : 'less-important'])}>{label}</span>
	</FileNameWrapper>
}

interface FileNameWrapperProps extends React.Props<{}> {
	label:string
	iconPath:string
	isImportant:boolean
}

const FileNameWrapper = (props:FileNameWrapperProps) =>
	<span className="file-name-wrapper">
		<span className={joinStrings([props.isImportant ? 'more-important' : 'less-important'])}>
			<img className="file-icon" src={props.iconPath} />
			<span className="file-name">{props.label} </span>
		</span>
		{props.children}
	</span>

interface FolderTree {
	folderId:number
	folderName:string
	allPotentialChildren:MemoryFolder[]
}

/**
 * For a given document in the memory module, how many times it was downloaded.
 */
const SingleFolderDownload = (props:FolderTree):JSX.Element => {

	const children = props.allPotentialChildren.filter((fileOrFolder:MemoryFolder) => fileOrFolder.parent_serial == props.folderId)
	const childFiles = children.filter((fileOrFolder:MemoryFolder) => !fileOrFolder.is_directory)
	const childFolders = children.filter((fileOrFolder:MemoryFolder) => fileOrFolder.is_directory)

	return <FileNameWrapper label={props.folderName} iconPath="/sb/static/images/icons/folder.png" isImportant={false}>
		<FileList files={childFiles} />
		<FolderList folders={childFolders} allPotentialChildren={props.allPotentialChildren} />
	</FileNameWrapper>
}

/**
 * For a single "Document Manager (Files/Folders)" spare field, how many times each file was downloaded (recursive).
 */
const MemoryFilesFoldersField = (props:FilesFoldersFieldStats) =>
	<div className="memory-files-folders-field">
		<FileList files={props.details.files} />
		<FolderHierarchyView folders={props.details.folders} />
	</div>

/**
 * Same as "MemoryFilesFoldersField", except it flattens the folder heirachy, takes all of the files from each folder,
 * and combines it with the list of files provided via props.files.
 */
const MemoryFilesFoldersFieldFlattened = (props:FilesFoldersFieldStats) => {
	const onlyFiles = (fileOrFolder:MemoryFolder) => !fileOrFolder.is_directory
	const reduce = (previous:MemoryFile[], current:FolderHierarchy) =>	previous.concat(current.hierarchy.filter(onlyFiles));
	const files = props.details.folders.reduce(reduce, [])

	return <div className="memory-files-folders-field">
		<FileList files={files} />
	</div>
}

const FileList = (props:{files:MemoryFile[]}) =>
	<ul className="file-list">
		{props.files.map((file:MemoryFile) =>
			<li key={`file-${file.id}`} className="file">
				<SingleFileDownload {...file} />
			</li>
		)}
	</ul>

const FolderHierarchyView = (props:{folders:FolderHierarchy[]}) =>
	<ul className="folder-list">
		{props.folders.map((folder:FolderHierarchy) =>
			<li key={`folder-${folder.folderId}`} className="folder">
				<SingleFolderDownload
					folderId={folder.folderId}
					folderName={folder.name}
					allPotentialChildren={folder.hierarchy}/>
			</li>)}
	</ul>

interface FolderListProps {
	allPotentialChildren:MemoryFolder[]
	folders:MemoryFolder[]
}

const FolderList = (props:FolderListProps) =>
	<ul className="folder-list">
		{props.folders.map((folder:MemoryFolder) =>
			<li key={`folder-${folder.title}`} className="folder">
				<SingleFolderDownload
					folderId={folder.id}
					folderName={folder.title}
					allPotentialChildren={props.allPotentialChildren}/>
			</li>
		)}
	</ul>



// WEBPACK FOOTER //
// ./src/news2/statistics.tsx